import { useEffect, useState } from 'react'
import { doc, getDoc, type Timestamp } from 'firebase/firestore'
import { db } from '../lib/firestore'

/**
 * The next Confidence Accelerator class date, from the dashboard.
 *
 * WHY THIS EXISTS
 * ---------------
 * This date was shown in three places by three different mechanisms, and two of
 * them ignored the dashboard entirely:
 *
 *   /accelerator   read settings/activeClass.classStartDate      (correct)
 *   homepage       computeNextClassDate() GUESSED a quarterly
 *                  schedule: Jan 1, Apr 1, Jul 1, Oct 1          (wrong)
 *   /start         the string "Next class: July 1" was HARDCODED (wrong forever)
 *
 * So changing the class date in the dashboard updated exactly one of the three
 * pages a visitor might land on. One hook, one source of truth, and the failure
 * mode of "someone added a fourth copy" is at least now obvious in review.
 *
 * NOTE ON PRERENDERING: scripts/prerender.mjs bakes whatever this returns at
 * BUILD time into the static HTML. Real visitors always see the current value
 * because this refetches on load, but a crawler reading raw HTML sees the last
 * deploy's copy. Redeploy after changing the class date.
 */

const SETTINGS_DOC = 'settings/activeClass'

/** "September 3" — matches the format the Accelerator page already used. */
function formatClassDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
}

export interface NextClassDate {
  /** Formatted date, or null when unset/unreadable. Never a guess. */
  label: string | null
  /** The raw date, for callers that need to compare or format differently. */
  date: Date | null
  loading: boolean
}

export function useNextClassDate(): NextClassDate {
  const [label, setLabel] = useState<string | null>(null)
  const [date, setDate] = useState<Date | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    getDoc(doc(db, ...(SETTINGS_DOC.split('/') as [string, string])))
      .then((snap) => {
        if (cancelled || !snap.exists()) return
        const ts = snap.data()?.classStartDate as Timestamp | undefined
        if (!ts?.toDate) return
        const d = ts.toDate()
        if (isNaN(d.getTime())) return
        setDate(d)
        setLabel(formatClassDate(d))
      })
      .catch(() => {
        // Offline, permission denied, missing doc. Callers fall back to their
        // own copy ("Coming Soon"). Deliberately NOT a computed guess: showing
        // a confidently wrong date is worse than showing none.
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  return { label, date, loading }
}
